/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { B2B } from '@/components/blocks/B2B';
import { pdpWishListContainerButtonSX } from '@/components/blocks/ProductDetails/styles/addToWishListMenuItem';
import { TYPES } from '@/data/constants/product';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { ID } from '@/data/types/Basic';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { productIsA } from '@/utils/productIsA';
import { FavoriteBorder } from '@mui/icons-material';
import {
	Button,
	Stack
} from '@mui/material';
import { FC, MouseEvent, useCallback, useContext, useMemo, useState } from 'react';
import { productDetailsWishListButtonSX, productDetailsWishListIconSX } from './styles/addToWishListMenuItem';
import { FavoriteSvg } from './AddedToWishlist';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { findDefaultWishlist } from '@/utils/findDefaultWishlist';
import { useNotifications } from '@/data/Content/Notifications';

type Props = {
	standalone?: boolean;
};

export const ProductDetailsAddToWishList: FC<Props> = ({ standalone = false }) => {

	//const { creationData, onCreate } = useWishLists();
	const localization = useLocalization('productDetail');
	const routes = useLocalization('Routes');
	const wishlistNLS = useLocalization('WishList');
	const { selection, wishLists, mutateWishlistData, addToDefaultWishlist, product, removeFromWishList, loginStatus } =
		useContext(ContentContext) as ReturnType<typeof useProductDetails> &
		ReturnType<typeof useSkuListTable>;
	const isBundle = productIsA(product, TYPES.bundle);
	const sku = !isBundle ? selection?.sku : product;
	const itemId: ID = product?.items?.at(0)?.id.toString() ?? '';
	const buyable = !isBundle ? selection?.buyable : true;
	const quantity = !isBundle ? selection?.quantity : 1;
	const { prices } = getProductDisplayInfo(sku, product);
	const disabled = !buyable || !prices?.offer;
	const dtId = useMemo(() => `wishlist-selection${standalone ? '-standalone' : ''}`, [standalone]);
	const [isLoading, setIsLoading] = useState(false);
	const router = useNextRouter();
	const defaultWishList = useMemo(() => findDefaultWishlist(wishLists), [wishLists]);
	const { showSuccessMessage } = useNotifications();

	const isFavorite = useMemo(() => {
		if (!loginStatus || !defaultWishList || !sku?.partNumber) {
			return false;
		}
		return !!defaultWishList?.item?.find((item) => item?.partNumber === sku?.partNumber);
	}, [loginStatus, defaultWishList, sku?.partNumber]);

	const redirectedAfterLogin = router.query?.addToWishList === 'true';
	
	if (redirectedAfterLogin && !isFavorite && !isLoading) {
		(async () => {
			try {
				if (!sku?.partNumber) return;
				setIsLoading(true);
				await addToDefaultWishlist(sku.partNumber, quantity)();
				mutateWishlistData();
			} catch (e) {
				console.error('Error adding to wishlist after login:', e);
			} finally {
				setIsLoading(false);
				const queryWithoutAddToWL = { ...router.query };
				delete queryWithoutAddToWL.addToWishList;
			//	delete queryWithoutAddToWL.viewport;
				router.replace({ pathname: router.pathname, query: queryWithoutAddToWL }, undefined, { shallow: true });
			}
		})();
	}
	const toggleFavorite = useCallback(
		async (event: MouseEvent<HTMLButtonElement>) => {
			try {
				if (isLoading) return;
				if (!loginStatus) {
					showSuccessMessage(wishlistNLS.Msgs.LoginMsgToAddToWishList.t());
					const redirectEnc = window.btoa(router.asPath);
					router.push({ pathname: routes.Login.route.t(), query: { redirect: redirectEnc, addToWishList:'true' } });
					return;
				}
				const partNumber = sku?.partNumber;
				if (!partNumber) return;

				setIsLoading(true);
				if (!isFavorite) {
					await addToDefaultWishlist(partNumber, quantity)();
				} else {
					const wishListId = defaultWishList?.externalIdentifier;

					if (!product?.id || !wishListId) {
						console.error('Missing data for wishlist item removal');
						return;
					}
					await removeFromWishList(partNumber, itemId, wishListId)();
					mutateWishlistData();
				}
			} catch (e) {
				console.error('Error toggling favorite:', e);
			} finally {
				setIsLoading(false);
			}
		},
		[isFavorite,loginStatus,isLoading,defaultWishList]
	);
	return (
		<B2B is={false}>
			<Stack sx={pdpWishListContainerButtonSX} alignItems="center" justifyContent="center">
				<Button
					sx={productDetailsWishListButtonSX}
					data-testid={dtId}
					id={dtId}
					onClick={toggleFavorite}
					disabled={disabled || isLoading}
					aria-label={localization.addToWL.t()}
					aria-pressed={isFavorite}
					role={'button'}
				>
					{isFavorite ? (
						<FavoriteSvg />
					) : (
						<FavoriteBorder sx={{ ...productDetailsWishListIconSX }} />
					)}
				</Button>
			</Stack>
		</B2B>
	);
};